"use client";

import { type ModuleTeamCarousel } from "@lib/types";
import { useEffect, useState } from "react";

import { CarouselIndicators } from "@/components/shared/carousel-indicators";
import PortableText from "@/components/shared/portable-text";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  type CarouselOptions,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/ui/cn";
import { getModuleColorTheme } from "@/lib/ui/colorTheme";

import { TeamCarouselCard } from "./TeamCarousel/Card";

type TeamCarouselModuleProps = {
  module: ModuleTeamCarousel;
};

const carouselOpts: CarouselOptions = {
  align: "start",
  slidesToScroll: 1,
  containScroll: "keepSnaps",
};

export function TeamCarouselModule({ module }: TeamCarouselModuleProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [buttonActivatedIndex, setButtonActivatedIndex] = useState<number | null>(null);

  const { themeClasses, isDarkTheme, proseClasses } = getModuleColorTheme(module);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateCurrent = () => {
      const newIndex = api.selectedScrollSnap();
      setCurrent(newIndex);
      // Clear hover state on scroll
      setHoveredIndex(null);
      // Set button-activated state immediately so card becomes active right away
      // This makes indicator clicks feel instant
      setButtonActivatedIndex(newIndex);
    };

    updateCurrent();

    api.on("select", updateCurrent);
  }, [api]);

  const handleMouseLeave = () => {
    // Only reset hover index, keep button-activated card active
    if (buttonActivatedIndex === null) {
      setHoveredIndex(null);
    } else {
      setHoveredIndex(buttonActivatedIndex);
    }
    setIsHovered(false);
  };

  // Get the active card index (hovered, button-activated, or current)
  const activeIndex =
    hoveredIndex !== null
      ? hoveredIndex
      : buttonActivatedIndex !== null
        ? buttonActivatedIndex
        : current;
  const totalItems = module.items?.length || 0;

  // Custom handlers that scroll based on active card index
  const handlePrevious = () => {
    if (!api || activeIndex === 0) return;
    const prevIndex = activeIndex - 1;
    api.scrollTo(prevIndex);
    setHoveredIndex(prevIndex);
    setButtonActivatedIndex(prevIndex);
  };

  const handleNext = () => {
    if (!api || activeIndex >= totalItems - 1) return;
    const nextIndex = activeIndex + 1;
    api.scrollTo(nextIndex);
    setHoveredIndex(nextIndex);
    setButtonActivatedIndex(nextIndex);
  };

  const canScrollPrev = activeIndex > 0;
  const canScrollNext = activeIndex < totalItems - 1;

  if (!module.items || module.items.length === 0) return null;

  const eyebrowBadge = module.eyebrow ? (
    <Badge variant="success" className="w-fit">
      {module.eyebrow}
    </Badge>
  ) : null;

  return (
    <Container
      as="section"
      className={cn("flex flex-col gap-8", themeClasses, "py-padding-section-lg")}
    >
      <div className="flex flex-col gap-6">
        <div className="max-w-3xl space-y-3">
          {eyebrowBadge}
          {module.title ? (
            <h3
              className={cn(
                "typography-heading-3",
                isDarkTheme ? "text-white" : "text-neutral-darkest",
              )}
            >
              {module.title}
            </h3>
          ) : null}
          {module.description ? (
            <div className="flex items-start justify-between gap-4">
              <PortableText
                className={cn(
                  "typography-text-large-normal prose prose-p:my-0 flex-1",
                  proseClasses,
                )}
                value={module.description}
              />
            </div>
          ) : null}
        </div>

        <CarouselIndicators
          api={api}
          current={hoveredIndex !== null ? hoveredIndex : current}
          count={module.items.length}
          className="max-tablet:hidden justify-end"
        />
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={handleMouseLeave}
          className="relative w-full"
        >
          <Carousel setApi={setApi} opts={carouselOpts}>
            <CarouselContent
              rootClassName="tablet:px-padding-global"
              className="tablet:gap-gap-element-md ml-0!"
            >
              {module.items.map((item, index) => (
                <CarouselItem
                  key={item._key}
                  className={cn(
                    "shrink-0 basis-auto pl-0",
                    "tablet:w-auto tablet:max-w-[420px] desktop:max-w-[544px] w-full",
                  )}
                >
                  <TeamCarouselCard
                    item={item}
                    isActive={activeIndex === index}
                    onHover={() => {
                      setHoveredIndex(index);
                      setButtonActivatedIndex(null);
                    }}
                    onTouch={() => {
                      setHoveredIndex(index);
                      setButtonActivatedIndex(null);
                    }}
                    textColorClasses={proseClasses}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              variant="outline"
              className={cn(
                "tablet:block hidden transition-opacity duration-300",
                isHovered ? "opacity-100" : "opacity-0",
                "top-1/2 left-0",
              )}
              onClick={handlePrevious}
              disabled={!canScrollPrev}
            />
            <CarouselNext
              variant="outline"
              className={cn(
                "tablet:block hidden transition-opacity duration-300",
                isHovered ? "opacity-100" : "opacity-0",
                "top-1/2 right-0",
              )}
              onClick={handleNext}
              disabled={!canScrollNext}
            />
          </Carousel>
        </div>
        <CarouselIndicators
          api={api}
          current={hoveredIndex !== null ? hoveredIndex : current}
          count={module.items.length}
          className="tablet:hidden justify-start"
        />
      </div>
    </Container>
  );
}
